<template>
  <b-container fluid id="main">
    <h1>User information</h1>
    <h3 style="text-align: center" v-if="!localStore.loading && localStore.userIdentity === null">User not found</h3>

    <b-row>
      <b-col lg="3" v-if="localStore.userIdentity !== null">
        <p>Identity:</p>
        <b-form inline>
          <b-input placeholder="Identity (base64)" disabled="disabled" inline :value="localStore.userIdentity"/>
          <b-btn variant="primary" @click="copyIdentity"><b-icon-clipboard-check scale=".8"/></b-btn>
        </b-form>
      </b-col>
      <b-col lg="3" v-if="localStore.userApiKey !== null">
        <p>API Key:<span id="apiKeyType" v-if="localStore.userApiKeyType !== null">{{apiKeyType}}</span></p>
        <b-form inline>
          <b-input placeholder="API Key" disabled="disabled" inline :value="localStore.userApiKey"/>
          <b-btn variant="primary" @click="copyApiKey"><b-icon-clipboard-check scale=".8"/></b-btn>
        </b-form>
      </b-col>
      <b-col lg="3" v-if="localStore.userApiKey !== null">
        <b-form-group label="Permissions:">
          <b-form-checkbox
              id="permission-checkbox-call"
              v-model="localStore.userCanCall"
              disabled="disabled"
              inline
          >Call</b-form-checkbox>
          <b-form-checkbox
              id="permission-checkbox-multi-device"
              v-model="localStore.userCanMultiDevice"
              disabled="disabled"
              inline
          >Multi-device</b-form-checkbox>
        </b-form-group>
      </b-col>
    </b-row>

    <h1 class="pt-5">Devices</h1>
    <b-row>
      <b-col>
        <b-table striped hover no-sort-reset :busy="localStore.loading" :items="localStore.userDevices === null ? [] : Object.values(localStore.userDevices)" :fields="fields"  primary-key="uid" sort-by="lastOnline" sort-desc>
          <template v-slot:cell(uid)="device">
            {{ device.item.uid.substr(0, 15) }}… <b-icon-clipboard-check variant="primary" @click="copy(device.item.uid)"/>
          </template>

          <template v-slot:cell(active)="device">
            <b-icon-check v-if="device.item.active" variant="success" scale="2"/>
            <span v-else><b-icon-x variant="danger" scale="2" /> <reactivate-device-button scale=".5" v-if="localStore.userIdentity && device.item.uid" :identity="localStore.userIdentity" :device-uid="device.item.uid" :on-success="reload"/></span>
          </template>

          <template v-slot:cell(pushToken)="device">
            <span v-if="device.item.pushToken">{{ device.item.pushToken.substr(0, 15) }}… <b-icon-clipboard-check variant="primary" @click="copy(device.item.pushToken)"/></span>
            <span v-else>-</span>
          </template>

          <template v-slot:table-busy>
            <div class="text-center text-danger">
              <b-spinner class="align-middle"></b-spinner>
              <strong> Loading...</strong>
            </div>
          </template>
        </b-table>
        <h3 style="text-align: center" v-if="!localStore.loading && localStore.userDevices.constructor === Object && Object.keys(localStore.userDevices).length === 0">No device found</h3>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import {API} from "aws-amplify";
import {formatDate, store} from "@/main"
import ReactivateDeviceButton from "@/components/buttons/ReactivateDeviceButton.vue";

const localStore = {
  loading: false,
  userIdentity: null,
  userDevices: {},
  userApiKey: null,
  userApiKeyType: null,
  userCanCall: null,
  userCanMultiDevice: null,
}

const loadUser = function (escapedIdentity) {
  localStore.loading = true
  localStore.userIdentity = null
  localStore.userDevices = {}
  localStore.userApiKey = null
  localStore.userApiKeyType = null
  localStore.userCanCall = null
  localStore.userCanMultiDevice = null

  const identity = decodeURIComponent(escapedIdentity).replace(/\s+/g, '')
  // first determine the kind of identity (hex, invitation, base64) and always convert to base64 version
  let identityBase64 = null;
  let apiKey = null;
  if (identity.startsWith("https://invitation.olvid.io")) {
    // invitation link
    identityBase64 = invitationToIdentityBase64(identity)
  } else if (/^[0-9a-f]+$/i.test(identity)) {
    // hex identity
    identityBase64 = btoa(String.fromCharCode(...(new Uint8Array(identity.match(/.{1,2}/g).map(byte => parseInt(byte, 16))))))
  } else if (/^[0-9a-f-]+$/i.test(identity)) {
    // api key
    apiKey = identity
  } else {
    // we assume this is already base64
    identityBase64 = identity
  }

  API.post(store.state.api, "/get", {body: JSON.stringify({
      q: 9,
      identity: identityBase64,
      apiKey: apiKey,
    }),
  })
      .then(response => {
        localStore.loading = false
        localStore.userIdentity = response.identity || null
        let devices = {}
        if (response.devices) {
          for (let device of response.devices) {
            devices[device.uid] = device
          }
        }
        localStore.userDevices = devices
        localStore.userApiKey = response.apiKey || null
        localStore.userApiKeyType = response.apiKeyType || null
        localStore.userCanCall = response.canCall || null
        localStore.userCanMultiDevice = response.canMultiDevice || null
      })
      .catch(() => {
        localStore.loading = false
        localStore.userIdentity = null
        localStore.userDevices = {}
        localStore.userApiKey = null
        localStore.userApiKeyType = null
        localStore.userCanCall = null
        localStore.userCanMultiDevice = null
      });
}

export default {
  name: "UserDetails",
  components: {ReactivateDeviceButton},
  props: {
    identity: String,
  },
  data() {
    return {
      localStore: localStore,
      storeState: store.state,
      fields: [
        {
          key: 'uid',
          label: 'Device UID',
          sortable: true,
        },
        {
          key: 'active',
          label: 'Active',
          sortable: true,
        },
        {
          key: 'expiration',
          label: 'Device Expiration',
          sortable: true,
          formatter: timestamp => {
            return timestamp ? formatDate(new Date(timestamp)) : '-'
          }
        },
        {
          key: 'lastOnline',
          label: 'Last Online',
          sortable: true,
          formatter: timestamp => {
            return timestamp ? formatDate(new Date(timestamp)) : '-'
          }
        },
        {
          key: 'lastMessage',
          label: 'Last Message',
          sortable: true,
          formatter: timestamp => {
            return timestamp ? formatDate(new Date(timestamp)) : '-'
          }
        },
        {
          key: 'os',
          label: 'Platform',
          sortable: true,
        },
        {
          key: 'pushToken',
          label: 'Push Token',
          sortable: false,
        },
      ],
    }
  },
  computed: {
    prod() {
      return store.state.prod;
    },
    apiKeyType() {
      if (localStore.userApiKeyType === 1) {
        return "Admin app"
      } else if (localStore.userApiKeyType === 2) {
        return "Store"
      } else if (localStore.userApiKeyType === 3) {
        return "In-app purchase"
      }
      return ""
    },
  },
  methods: {
    reload() {
      loadUser(this.identity)
    },
    copyIdentity() {
      navigator.clipboard.writeText(localStore.userIdentity)
      this.$bvToast.toast('Identity copied to clipboard', {
        variant: "success",
        toaster: "b-toaster-top-center",
        autoHideDelay: 1000,
        noCloseButton: true,
      })
    },
    copyApiKey() {
      navigator.clipboard.writeText(localStore.userApiKey)
      this.$bvToast.toast('API Key copied to clipboard', {
        variant: "success",
        toaster: "b-toaster-top-center",
        autoHideDelay: 1000,
        noCloseButton: true,
      })
    },
    copy(stuff) {
      navigator.clipboard.writeText(stuff)
      this.$bvToast.toast('Copied to clipboard', {
        variant: "primary",
        toaster: "b-toaster-top-center",
        autoHideDelay: 500,
        noCloseButton: true,
      })
    }
  },
  mounted() {
    loadUser(this.identity)
  },
  watch: {
    prod() {
      loadUser(this.identity)
    }
  }
}



function b64ToUint6(nChr) {
  return nChr > 64 && nChr < 91
      ? nChr - 65
      : nChr > 96 && nChr < 123
          ? nChr - 71
          : nChr > 47 && nChr < 58
              ? nChr + 4
              : nChr === 45
                  ? 62
                  : nChr === 95
                      ? 63
                      : 0;
}

function base64Decode(sBase64) {
  let sB64Enc = sBase64.replace(/[^A-Za-z0-9_-]/g, ""),
      nInLen = sB64Enc.length,
      nOutLen = (nInLen * 3 + 1) >>> 2,
      aBytes = new Uint8Array(nOutLen);

  for (let nMod3, nMod4, nUint24 = 0, nOutIdx = 0, nInIdx = 0; nInIdx < nInLen; nInIdx++) {
    nMod4 = nInIdx & 3;
    nUint24 |= b64ToUint6(sB64Enc.charCodeAt(nInIdx)) << (18 - 6 * nMod4);
    if (nMod4 === 3 || nInLen - nInIdx === 1) {
      for (nMod3 = 0; nMod3 < 3 && nOutIdx < nOutLen; nMod3++, nOutIdx++) {
        aBytes[nOutIdx] = (nUint24 >>> ((16 >>> nMod3) & 24)) & 255;
      }
      nUint24 = 0;
    }
  }
  return aBytes;
}

const invitationToIdentityBase64 = function(url) {
  const binary = base64Decode(new URL(url).hash.slice(1))
  if (binary[0] !== 3) return null
  if (binary[5] !== 0) return null
  const identityLength = (binary[6] << 24) + (binary[7] << 16) + (binary[8] << 8) + binary[9]
  return  btoa(String.fromCharCode(...(binary.slice(10, 10 + identityLength))))
}

</script>

<style scoped>
#main {
    padding-top: 15px;
}
.buttons > * {
    margin-left: 10px;
}
p {
  margin-bottom: 4px;
}
#apiKeyType {
  margin-left: 10px;
  font-weight: bold;
  color: #559955;
}
</style>